import { nextTick, reactive } from 'vue'
import { gsap } from 'gsap'
import { Flip } from 'gsap/Flip'

gsap.registerPlugin(Flip)

// Only the real, entering route is teleported into this expanding surface.
// The outgoing route is inert and retained briefly by Vue's Transition.
export const portal = reactive({
  open: false,
  lifted: false,
  to: '',
  label: '',
  height: 0,
})

export interface PortalFlight {
  mount(): void
  reveal(position: { top: number; left: number }): void
  cancel(): void
}

interface Flight extends PortalFlight {
  cancelled: boolean
  context: gsap.Context
  leaves: Set<() => void>
}

let current: Flight | undefined

/** GSAP Flip owns the measurements, projection, interpolation and easing.
 * No bounding-box arithmetic, hand-written FLIP, canvas copies or fake pages. */
export function openPagePortal(
  origin: HTMLElement,
  to: string,
  label: string,
  navigate: () => void,
  complete: () => void,
): PortalFlight | undefined {
  const surface = document.getElementById('route-portal')
  const content = document.getElementById('route-portal-content')
  if (!surface || !content) return

  const previousId = origin.getAttribute('data-flip-id')
  origin.setAttribute('data-flip-id', 'route-portal')
  const state = Flip.getState(origin, { props: 'borderRadius,backgroundColor' })
  if (previousId === null) origin.removeAttribute('data-flip-id')
  else origin.setAttribute('data-flip-id', previousId)

  let geometryDone = false
  let contentDone = false
  let finishing = false
  let position = { top: 0, left: 0 }
  const context = gsap.context(() => {})
  const flight: Flight = {
    cancelled: false,
    context,
    leaves: new Set(),
    mount() {
      if (!flight.cancelled && current === flight) portal.lifted = true
    },
    reveal(savedPosition) {
      if (flight.cancelled || current !== flight) return
      position = savedPosition
      content.scrollTop = position.top
      content.scrollLeft = position.left
      context.add(() => {
        gsap.fromTo(
          content,
          { opacity: 0, y: 28 },
          {
            opacity: 1,
            y: 0,
            duration: 0.32,
            delay: 0.03,
            ease: 'power2.out',
            onComplete() {
              contentDone = true
              void finish()
            },
          },
        )
      })
    },
    cancel() {
      if (flight.cancelled) return
      flight.cancelled = true
      context.revert()
      flight.leaves.forEach((done) => done())
      flight.leaves.clear()
      if (current === flight) {
        portal.lifted = false
        portal.open = false
        current = undefined
      }
      navigate()
      void nextTick().then(() =>
        document.dispatchEvent(new Event('cqu:page-layer-change')),
      )
    },
  }

  async function finish() {
    if (!geometryDone || !contentDone || finishing || flight.cancelled) return
    finishing = true
    flight.leaves.forEach((done) => done())
    flight.leaves.clear()
    portal.lifted = false
    // Teleport moves the same DOM back; forms and WebGL are not remounted.
    await nextTick()
    if (current !== flight || flight.cancelled) return
    window.scrollTo({ ...position, behavior: 'instant' })
    portal.open = false
    context.revert()
    current = undefined
    document.dispatchEvent(new Event('cqu:page-layer-change'))
    complete()
  }

  current = flight
  portal.height = document.documentElement.scrollHeight
  portal.to = to
  portal.label = label
  portal.open = true
  // Prepare the old scene before Vue unmounts its component. Its retained
  // transition DOM then contains the SVG, not an already-disposed canvas.
  document.dispatchEvent(
    new CustomEvent('cqu:page-layer-change', { detail: { preparing: true } }),
  )
  void nextTick()
    .then(() => {
      if (current !== flight || flight.cancelled) return
      context.add(() => {
        gsap.set(content, { opacity: 0 })
        const timeline = gsap.timeline({
          onComplete() {
            geometryDone = true
            void finish()
          },
        })
        timeline.add(
          Flip.from(state, {
            targets: surface,
            scale: true,
            nested: true,
            props: 'borderRadius,backgroundColor',
            duration: 0.86,
            ease: 'power3.inOut',
          }),
          0,
        )
        timeline.fromTo(
          '.portal-seed-label',
          { opacity: 1 },
          {
            opacity: 0,
            duration: 0.14,
          },
          0.18,
        )
        // Route chunks are already resolved. Begin mounting while the card grows.
        timeline.call(navigate, [], 0.1)
      })
    })
    .catch(() => {
      flight.cancel()
      complete()
    })
  return flight
}

/** Used by Vue's Transition, not by a cloned DOM or an extra RouterView. */
export function leavePortalPage(element: Element, done: () => void) {
  const flight = current
  if (!flight || !portal.open || flight.cancelled) {
    done()
    return
  }
  const page = element as HTMLElement
  page.inert = true
  page.setAttribute('aria-hidden', 'true')
  document.dispatchEvent(new Event('cqu:page-layer-change'))
  const finish = () => {
    flight.leaves.delete(finish)
    done()
  }
  flight.leaves.add(finish)
  flight.context.add(() => {
    gsap
      .timeline({ onComplete: finish })
      .to(page, {
        opacity: 0.38,
        scale: 0.96,
        y: 12,
        transformOrigin: '50% 0%',
        duration: 0.52,
        ease: 'power1.inOut',
      })
      .to(page, { opacity: 0, duration: 0.12 })
  })
}

if (import.meta.hot) import.meta.hot.dispose(() => current?.cancel())
